import type { FieldExplainerContent } from './types';

export type RentVsBuyExplainerContext = {
  input: {
    monthlyRent: number;
    rentIncrease: number;
    rentersInsurance: number;
    investmentReturn: number;
    homePrice: number;
    downPayment: number;
    interestRate: number;
    loanTermYears: number;
    propertyTax: number;
    homeInsurance: number;
    maintenancePercent: number;
    homeAppreciation: number;
    closingCostsBuy: number;
    sellingCostsPercent: number;
    timeHorizon: number;
  };
  result: {
    loanAmount: number;
    monthlyMortgage: number;
    totalRentPaid: number;
    totalRentersInsurance: number;
    investmentGains: number;
    totalRentCost: number;
    totalMortgagePayments: number;
    totalPropertyTax: number;
    totalHomeInsurance: number;
    totalMaintenance: number;
    futureHomeValue: number;
    remainingLoanBalance: number;
    sellingCosts: number;
    /** Future value minus the loan still owed, before selling costs. */
    grossSaleValue: number;
    /** Cash in hand at closing, after selling costs. */
    saleProceeds: number;
    totalBuyCost: number;
    netBuyCost: number;
    recommendation: 'rent' | 'buy';
    breakEvenYear: number | null;
  };
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const usdCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

/** Escalation rates the engine applies to owner costs. Neither is adjustable on the page. */
const PROPERTY_TAX_ESCALATION = 0.02;
const HOME_INSURANCE_ESCALATION = 0.03;
/** Years the break-even search scans, independent of the horizon you set. */
const BREAK_EVEN_SCAN_YEARS = 30;

function rate(value: number): string {
  return `${Number(value.toFixed(3))}%`;
}

export function getRentVsBuyFieldExplainers(ctx: RentVsBuyExplainerContext): FieldExplainerContent[] {
  const {
    monthlyRent,
    rentIncrease,
    rentersInsurance,
    investmentReturn,
    homePrice,
    downPayment,
    interestRate,
    loanTermYears,
    propertyTax,
    homeInsurance,
    maintenancePercent,
    homeAppreciation,
    closingCostsBuy,
    sellingCostsPercent,
    timeHorizon
  } = ctx.input;

  const {
    loanAmount,
    monthlyMortgage,
    totalRentPaid,
    totalRentersInsurance,
    investmentGains,
    totalRentCost,
    totalMortgagePayments,
    totalPropertyTax,
    totalHomeInsurance,
    totalMaintenance,
    futureHomeValue,
    remainingLoanBalance,
    sellingCosts,
    grossSaleValue,
    saleProceeds,
    totalBuyCost,
    netBuyCost,
    recommendation,
    breakEvenYear
  } = ctx.result;

  const lastYear = Math.max(1, timeHorizon);
  const finalMonthlyRent = monthlyRent * Math.pow(1 + rentIncrease / 100, lastYear - 1);
  const investedLump = downPayment + closingCostsBuy;
  const monthsCharged = Math.min(timeHorizon * 12, loanTermYears * 12);
  const monthlyRatePeriods = loanTermYears * 12;
  const monthlyRateDecimal = interestRate / 100 / 12;
  const firstYearMaintenance = homePrice * (maintenancePercent / 100);
  const finalYearMaintenance = firstYearMaintenance * Math.pow(1 + homeAppreciation / 100, lastYear - 1);
  const firstYearCarry = monthlyMortgage + (propertyTax + homeInsurance + firstYearMaintenance) / 12;
  const finalPropertyTax = propertyTax * Math.pow(1 + PROPERTY_TAX_ESCALATION, lastYear - 1);
  const finalHomeInsurance = homeInsurance * Math.pow(1 + HOME_INSURANCE_ESCALATION, lastYear - 1);
  const breakEvenText =
    breakEvenYear === null
      ? `buying never becomes cheaper within ${BREAK_EVEN_SCAN_YEARS} years`
      : `buying first becomes cheaper in year ${breakEvenYear}`;

  return [
    {
      id: 'monthlyRent',
      kind: 'input',
      label: 'Monthly rent',
      plain:
        'What you pay today. The model charges twelve months at this figure in year one, then raises it once at the start of every year that follows.',
      formula: `year 1: ${usd.format(monthlyRent)} × 12 = ${usd.format(monthlyRent * 12)}\nyear ${lastYear}: ${usd.format(finalMonthlyRent)} × 12\n\n${lastYear} years of rent = ${usd.format(totalRentPaid)}`,
      feeds: 'The rent line on the chart and in the table, and the largest single item inside the rent path cost.'
    },
    {
      id: 'rentIncrease',
      kind: 'input',
      label: 'Annual rent increase',
      plain:
        'Compounds the rent once a year. Because it multiplies a payment you make every month, a change of half a point here moves the comparison more than it looks like it should.',
      formula: `rent(year n) = ${usd.format(monthlyRent)} × (1 + ${rate(rentIncrease)})^(n − 1)\n\nyear 1: ${usd.format(monthlyRent)}/mo\nyear ${lastYear}: ${usd.format(finalMonthlyRent)}/mo`,
      feeds: 'How steeply the rent line climbs, and therefore where it crosses the buy line.',
      assumption:
        'One steady rate forever. Real leases move in steps, sometimes not at all for a year, and moving to a different unit resets the whole thing.'
    },
    {
      id: 'rentersInsurance',
      kind: 'input',
      label: 'Renter insurance (annual)',
      plain:
        'A flat annual charge on the rent path. It is the one recurring cost on this page that is never escalated — the same figure is billed every year.',
      formula: `${usd.format(rentersInsurance)} × ${lastYear} years = ${usd.format(totalRentersInsurance)}`,
      feeds: 'The rent path cost. On most inputs it is the smallest line item on the page.',
      assumption: `Home insurance on the buy side is escalated ${rate(HOME_INSURANCE_ESCALATION * 100)} a year but renters insurance is not. That asymmetry is built into the engine and is not adjustable here.`
    },
    {
      id: 'investmentReturn',
      kind: 'input',
      label: 'Investment return',
      plain:
        'The rate the renter earns on the money a buyer would sink into the purchase. Only the gains are credited — the principal is untouched on both sides, so it cancels out.',
      formula: `invested = ${usd.format(downPayment)} down + ${usd.format(closingCostsBuy)} closing\n         = ${usd.format(investedLump)}\n\ngains = ${usd.format(investedLump)} × (1 + ${rate(investmentReturn)})^${lastYear} − ${usd.format(investedLump)}\n      = ${usd.format(investmentGains)}`,
      feeds: 'The rent path cost, which subtracts these gains. Raising this rate pushes the break-even year further out.',
      assumption:
        'Only the up-front lump is invested. The monthly gap between rent and the full cost of owning is not invested on either side, so a renter who also banks that difference does better than this page shows.'
    },
    {
      id: 'homePrice',
      kind: 'input',
      label: 'Home price',
      plain:
        'One input driving four separate things: the loan size, the appreciation base, the maintenance base, and the sale price at the end of your horizon.',
      formula: `loan          = ${usd.format(homePrice)} − ${usd.format(downPayment)} = ${usd.format(loanAmount)}\nvalue at yr ${lastYear} = ${usd.format(homePrice)} × (1 + ${rate(homeAppreciation)})^${lastYear}\n              = ${usd.format(futureHomeValue)}\nselling costs = ${rate(sellingCostsPercent)} × ${usd.format(futureHomeValue)} = ${usd.format(sellingCosts)}`,
      feeds: 'Every buy-side number on the page, from the monthly payment to the sale at the end.'
    },
    {
      id: 'downPayment',
      kind: 'input',
      label: 'Down payment',
      plain:
        'Cash out of pocket on day one. It shrinks the loan on the buy side, and on the rent side the identical amount is assumed invested instead.',
      formula:
        downPayment > homePrice
          ? `down payment capped at the price:\n  ${usd.format(homePrice)} goes into the house,\n  ${usd.format(downPayment - homePrice)} never leaves your pocket\nloan = ${usd.format(0)}\n\nboth paths ignore the surplus equally`
          : `loan = ${usd.format(homePrice)} − ${usd.format(downPayment)} = ${usd.format(loanAmount)}\n\nbuy path:  ${usd.format(downPayment)} counted as cash out,\n           returned later through the sale\nrent path: invested at ${rate(investmentReturn)} alongside closing costs`,
      feeds: 'The loan amount and monthly payment, and the size of the balance the renter is assumed to be investing.',
      assumption:
        'No mortgage insurance is modeled. A down payment under 20% costs nothing extra on this page, which flatters low-down-payment scenarios against renting.'
    },
    {
      id: 'interestRate',
      kind: 'input',
      label: 'Mortgage rate',
      plain:
        'Prices the loan with a standard fixed-rate amortization. A rate of zero is handled as straight-line repayment rather than falling over on a division.',
      formula:
        interestRate > 0
          ? `r = ${rate(interestRate)} / 12 = ${monthlyRateDecimal.toFixed(6)}\nn = ${loanTermYears} × 12 = ${monthlyRatePeriods}\n\npayment = ${usd.format(loanAmount)} × r(1+r)^n / ((1+r)^n − 1)\n        = ${usdCents.format(monthlyMortgage)}`
          : `at 0% the amortization formula does not apply;\nthe loan repays straight-line instead:\n\npayment = ${usd.format(loanAmount)} / ${monthlyRatePeriods} months\n        = ${usdCents.format(monthlyMortgage)}`,
      feeds: 'The Monthly mortgage card, and the total payments folded into the buy path cost.',
      assumption:
        'Interest is never deducted anywhere in this model. There is no mortgage-interest deduction, no property-tax deduction, and no capital-gains exclusion when the home is sold.'
    },
    {
      id: 'loanTermYears',
      kind: 'input',
      label: 'Loan term (years)',
      plain:
        'Sets the payment size and the point payments stop. If your horizon runs past the payoff date the model stops charging payments — it never charges more months than the term.',
      formula: `months charged = min(${timeHorizon} × 12, ${loanTermYears} × 12) = ${monthsCharged}\ntotal payments = ${usdCents.format(monthlyMortgage)} × ${monthsCharged}\n               = ${usd.format(totalMortgagePayments)}\n\nstill owed at year ${lastYear}: ${usd.format(remainingLoanBalance)}\n  (settled out of the sale)`,
      feeds: 'The monthly payment, the total payments inside the buy path cost, and the loan balance netted out of the sale.'
    },
    {
      id: 'propertyTax',
      kind: 'input',
      label: 'Property tax (annual)',
      plain:
        'Your first-year tax bill. The engine raises it every year at a fixed rate that is not exposed as an input.',
      formula: `year 1: ${usd.format(propertyTax)}\nyear n: ${usd.format(propertyTax)} × ${(1 + PROPERTY_TAX_ESCALATION).toFixed(2)}^(n − 1)\nyear ${lastYear}: ${usd.format(finalPropertyTax)}\n\n${lastYear}-year total: ${usd.format(totalPropertyTax)}`,
      feeds: 'The buy path cost. Over a long horizon it is often the second-largest owner expense after mortgage interest.',
      assumption: `The ${rate(PROPERTY_TAX_ESCALATION * 100)} escalation is hardcoded. In states with assessment caps that is roughly right; after a reassessment, or in a state without a cap, it can be badly low.`
    },
    {
      id: 'homeInsurance',
      kind: 'input',
      label: 'Home insurance (annual)',
      plain:
        'Your first-year premium. It is escalated faster than the property-tax assumption, and like that one it is fixed in the engine rather than set here.',
      formula: `year 1: ${usd.format(homeInsurance)}\nyear n: ${usd.format(homeInsurance)} × ${(1 + HOME_INSURANCE_ESCALATION).toFixed(2)}^(n − 1)\nyear ${lastYear}: ${usd.format(finalHomeInsurance)}\n\n${lastYear}-year total: ${usd.format(totalHomeInsurance)}`,
      feeds: 'The buy path cost.'
    },
    {
      id: 'maintenancePercent',
      kind: 'input',
      label: 'Maintenance %',
      plain:
        'Charged each year against that year’s home value rather than the original price, so as the home appreciates the upkeep charge grows with it.',
      formula: `year n = ${rate(maintenancePercent)} × ${usd.format(homePrice)} × (1 + ${rate(homeAppreciation)})^(n − 1)\n\nyear 1: ${usd.format(firstYearMaintenance)}\nyear ${lastYear}: ${usd.format(finalYearMaintenance)}\n\n${lastYear}-year total: ${usd.format(totalMaintenance)}`,
      feeds: 'The buy path cost, and the gap between the mortgage payment and what owning actually costs each month.',
      assumption:
        'A steady annual percentage stands in for something genuinely lumpy — nothing for four years, then a roof. Over a short horizon the timing of that spend matters more than the average.'
    },
    {
      id: 'homeAppreciation',
      kind: 'input',
      label: 'Home appreciation',
      plain:
        'Compounds the home value once a year. It cuts both ways: it raises the sale price, but it also raises the selling-cost bill and every year’s maintenance charge.',
      formula: `value(year ${lastYear}) = ${usd.format(homePrice)} × (1 + ${rate(homeAppreciation)})^${lastYear}\n                = ${usd.format(futureHomeValue)}\n\nselling costs   = ${rate(sellingCostsPercent)} × ${usd.format(futureHomeValue)} = ${usd.format(sellingCosts)}\nmaintenance base rises at the same rate`,
      feeds: 'The sale side of the buy path, and the break-even year more than any other single input on this page.',
      assumption:
        'One smooth rate with no down years. Because the model always sells at the end of your horizon, a bad final year has nowhere to hide — there is no option to wait it out.'
    },
    {
      id: 'closingCostsBuy',
      kind: 'input',
      label: 'Closing costs (buy)',
      plain:
        'Up-front purchase costs. They are charged on the buy path and, on the rent side, added to the lump the renter invests — so they hit the comparison from both directions.',
      formula: `buy path:  + ${usd.format(closingCostsBuy)} cash out\nrent path: joins the invested lump\n           ${usd.format(downPayment)} + ${usd.format(closingCostsBuy)} = ${usd.format(investedLump)} at ${rate(investmentReturn)}`,
      feeds: 'Both path costs, which is why this field moves the comparison harder than its size suggests.'
    },
    {
      id: 'sellingCostsPercent',
      kind: 'input',
      label: 'Selling costs %',
      plain:
        'Agent commission and transaction costs, charged against the sale price rather than what you paid. This is the main reason a short ownership horizon is expensive even when the house went up.',
      formula: `${rate(sellingCostsPercent)} × ${usd.format(futureHomeValue)} = ${usd.format(sellingCosts)}\n\ncharged once, inside the buy path cost\nthe sale then releases\n  ${usd.format(futureHomeValue)} − ${usd.format(remainingLoanBalance)} owed\n  = ${usd.format(grossSaleValue)}`,
      feeds: 'The buy path cost, and how far out the break-even year lands.',
      assumption:
        'Charged in full at the end of your horizon, on the assumption you sell. If you plan to hold indefinitely, set this to 0 to see the version of the comparison where the transaction never happens.'
    },
    {
      id: 'timeHorizon',
      kind: 'input',
      label: 'Time horizon (years)',
      plain:
        'How long you stay before selling. Every figure on this page is cumulative through this year, and the sale is forced to happen at the end of it.',
      formula: `horizon ${timeHorizon} years\n  rent path ${usd.format(totalRentCost)}\n  buy path  ${usd.format(netBuyCost)}\n\n${breakEvenText}`,
      feeds: `Both cost cards, the recommendation, and the year-${timeHorizon} row of the table your answer is read from — the table itself always runs at least 15 years, so its last row can sit past your horizon.`,
      assumption: `The break-even search always scans years 1 through ${BREAK_EVEN_SCAN_YEARS} no matter what you set here, so the recommendation and the break-even year answer different questions — one is about your horizon, the other about the whole window.`
    },
    {
      id: 'recommendation',
      kind: 'result',
      label: 'Recommendation',
      plain:
        'A straight comparison of the two path costs at your horizon. It is not a confidence score: a gap of a few hundred dollars and a gap of fifty thousand both read the same way.',
      formula: `rent path ${usd.format(totalRentCost)}\nbuy path  ${usd.format(netBuyCost)}\n\n${recommendation === 'rent' ? 'rent is lower → “Rent”' : 'buy is lower or equal → “Buy”'}\n(an exact tie is reported as Buy)`,
      feeds: 'The note under this card carries the break-even year, which answers the separate question of how long you would have to stay for buying to win.'
    },
    {
      id: 'totalRentCost',
      kind: 'result',
      label: 'Total rent path cost',
      plain:
        'What renting costs across the horizon, less what the down payment and closing costs earn if you invest them instead. It can go negative when those gains outrun the rent.',
      formula: `rent paid          ${usd.format(totalRentPaid)}\nrenters insurance  ${usd.format(totalRentersInsurance)}\ninvestment gains  −${usd.format(investmentGains)}\n                   ${usd.format(totalRentCost)}`,
      feeds: 'One half of the recommendation, plus the rent line on the chart and in the yearly table.'
    },
    {
      id: 'netBuyCost',
      kind: 'result',
      label: 'Net buy path cost',
      plain:
        'Every dollar ownership costs across the horizon, minus the equity the sale releases. The remaining loan is paid off out of that sale, and the selling costs are charged once.',
      formula: `down payment       ${usd.format(downPayment)}\nmortgage payments  ${usd.format(totalMortgagePayments)}\nproperty tax       ${usd.format(totalPropertyTax)}\nhome insurance     ${usd.format(totalHomeInsurance)}\nmaintenance        ${usd.format(totalMaintenance)}\nclosing costs      ${usd.format(closingCostsBuy)}\nselling costs      ${usd.format(sellingCosts)}\ntotal out          ${usd.format(totalBuyCost)}\n\n− sale less loan payoff  ${usd.format(grossSaleValue)}\n                   ${usd.format(netBuyCost)}\n\ncash in hand at closing: ${usd.format(saleProceeds)}`,
      feeds: 'The other half of the recommendation, plus the buy line on the chart and in the yearly table.',
      assumption:
        'A pre-tax cash comparison on both sides: no mortgage-interest or property-tax deduction, no capital-gains exclusion on the sale, and no mortgage insurance.'
    },
    {
      id: 'monthlyMortgage',
      kind: 'result',
      label: 'Monthly mortgage',
      plain:
        'Principal and interest only. Property tax, insurance, and maintenance are counted separately in the buy path cost, so this is not what actually leaves your account each month.',
      formula: `${usd.format(loanAmount)} at ${rate(interestRate)} over ${loanTermYears} years\n  = ${usdCents.format(monthlyMortgage)} a month\n\nadd year-one tax, insurance and upkeep:\n  (${usd.format(propertyTax)} + ${usd.format(homeInsurance)} + ${usd.format(firstYearMaintenance)}) / 12\n  → about ${usdCents.format(firstYearCarry)} a month all in\n  against ${usd.format(monthlyRent)} of rent`,
      feeds: 'The total mortgage payments inside the buy path cost, charged for at most the length of the loan term.'
    }
  ];
}
